import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { CheckResourcesRequest, useCheckResources } from '@katalon-studio/pdp-react';
import { CheckResourcesResponse } from '@katalon-studio/pdp-react/types/checkResponse';
import { AsyncResult } from '@katalon-studio/pdp-react/utils/RequestUtils';
import { CheckResourcesResult } from '@katalon-studio/pdp-react/types';
import { PermissionAction, ResourcePolicy } from '../models/permission';

interface PermissionProps {
  loading: boolean;
  permissionMapping: Map<ResourcePolicy, PermissionAction[]>;
}

const defaultValue: PermissionProps = {
  loading: false,
  permissionMapping: new Map(),
};

const PermissionContext = createContext<PermissionProps>(defaultValue);

export const PermissionProvider = (
  { env, accountId, children }: { env: string, accountId: string | null, children: ReactNode },
) => {
  const [permissionMapping, setPermissionMapping] = useState(
    defaultValue.permissionMapping,
  );
  const [loading, setLoading] = useState(true);

  let checkResourceResponse: AsyncResult<CheckResourcesResult> | undefined;
  if (env !== 'development') {
    const resourceRequest: CheckResourcesRequest = {
      resources: Object.values(ResourcePolicy).map(resource => ({
        resource: {
          kind: resource,
        },
        actions: Object.values(PermissionAction),
      })),
    };
    checkResourceResponse = useCheckResources(resourceRequest, Number(accountId) ?? undefined);
  }

  const getDefaultPermissionMapping = () => {
    const permissionMapping: Map<ResourcePolicy, PermissionAction[]> = new Map();
    Object.entries(ResourcePolicy).forEach(([, value]) => {
      permissionMapping.set(value, Object.values(PermissionAction));
    });
    setPermissionMapping(permissionMapping);
  };

  useEffect(() => {
    if (!loading) {
      return;
    }

    // in dev env, we don't have permission check, so we just set all permissions to allow
    if (env === 'development') {
      getDefaultPermissionMapping();
      setLoading(false);
    }

    // in other envs, we get permission mapping from pdp
    if (checkResourceResponse?.data) {
      const permissionMapping: Map<ResourcePolicy, PermissionAction[]> = new Map();
      const checkResponse = (checkResourceResponse.data as any) as CheckResourcesResponse;
      checkResponse.results?.forEach(resourceActionsResponse => {
        permissionMapping.set(
          resourceActionsResponse.resource.kind as ResourcePolicy,
          Object.entries(resourceActionsResponse.actions)
            .filter(([, effect]) => effect.toString() === 'EFFECT_ALLOW')
            .map(([action]) => action as PermissionAction),
        );
      });

      setPermissionMapping(permissionMapping);
      setLoading(false);
    }
  }, [loading, checkResourceResponse]);

  return (
    <PermissionContext.Provider value={{ loading, permissionMapping }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => useContext(PermissionContext);
